import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface Favorite {
  fromUid: string;
  toUid: string;
  createdAt: string;
}

/** Deterministic id: one favorite per (fan, target) pair. */
export function favoriteId(fromUid: string, toUid: string): string {
  return `favorite_${fromUid}_to_${toUid}`;
}

export const favoriteService = {
  async favorite(fromUid: string, toUid: string): Promise<void> {
    await setDoc(doc(db, 'favorites', favoriteId(fromUid, toUid)), {
      fromUid,
      toUid,
      createdAt: new Date().toISOString(),
    });
  },

  async unfavorite(fromUid: string, toUid: string): Promise<void> {
    await deleteDoc(doc(db, 'favorites', favoriteId(fromUid, toUid)));
  },

  /** One-shot read of `fromUid`'s favorited profile uids, sorted. */
  async getMyFavoriteUids(fromUid: string): Promise<string[]> {
    const snap = await getDocs(query(collection(db, 'favorites'), where('fromUid', '==', fromUid)));
    const toUids: string[] = [];
    snap.forEach(d => toUids.push(d.data().toUid));
    return toUids.sort();
  },

  /** Live list of uids `fromUid` has favorited, sorted for stable query keys. */
  subscribeToMyFavorites(
    fromUid: string,
    callback: (toUids: string[]) => void,
    onError?: (error: Error) => void
  ) {
    const favoritesQuery = query(collection(db, 'favorites'), where('fromUid', '==', fromUid));
    return onSnapshot(
      favoritesQuery,
      snap => {
        const toUids: string[] = [];
        snap.forEach(d => toUids.push(d.data().toUid));
        callback(toUids.sort());
      },
      error => {
        console.error('Favorites subscription failed:', error);
        if (onError) {
          onError(error);
        } else {
          callback([]);
        }
      }
    );
  },
};
